import { WorkItem } from '../types/Work/WorkItem'
import { ComponentChangeSummary } from '../types/Work/Survey'
import { ReworkTask, WorkItemTask } from '../types/Work/WorkItemTask'
import { Constraint } from '../types/Work/Constraint'

export function buildDescendantMap(items: WorkItem[]): Map<number, number[]> {
	const map = new Map<number, number[]>()

	function dfs(node: WorkItem): number[] {
		const all: number[] = []
		for (const child of node.childrenWorkItems || []) {
			all.push(child.id, ...dfs(child))
		}
		map.set(node.id, all)
		return all
	}

	items.forEach((item) => dfs(item))
	return map
}

export function findLeafWorkItems(lastLayerWorkItems: WorkItem[], workItems?: WorkItem[]) {
	if (!workItems) return

	for (const workItem of workItems) {
		if (!workItem.childrenWorkItems || workItem.childrenWorkItems.length === 0) {
			lastLayerWorkItems.push(workItem)
		} else {
			findLeafWorkItems(lastLayerWorkItems, workItem.childrenWorkItems)
		}
	}
}

export function isTaskInHierarchy(
	hierarchy: WorkItem,
	taskId?: number,
	reworkId?: number,
): boolean {
	if (
		(taskId && hierarchy.tasks.some((task) => task.id === taskId)) ||
		(reworkId && hierarchy.reworks.some((rework) => rework.id === reworkId))
	) {
		return true
	}

	if (hierarchy.childrenWorkItems) {
		return hierarchy.childrenWorkItems.some((child) => isTaskInHierarchy(child, taskId, reworkId))
	}

	return false
}

export function extractComponentChangeSummaries(rootWorkItem: WorkItem): ComponentChangeSummary[] {
	const summaries: ComponentChangeSummary[] = []

	function traverseWorkItem(workItem: WorkItem, currentPath: string) {
		// Build the path for this work item
		const workItemPath = currentPath ? `${currentPath} > ${workItem.name}` : workItem.name

		// Check if this is a leaf node (no children)
		if (!workItem.childrenWorkItems || workItem.childrenWorkItems.length === 0) {
			// Extract ComponentChangeSummary from ComponentChangeOrder list
			if (workItem.componentChangeOrders && workItem.componentChangeOrders.length > 0) {
				workItem.componentChangeOrders.forEach((changeOrder) => {
					if (
						changeOrder.proposedValue !== null &&
						changeOrder.proposedValue !== changeOrder.originalValue
					) {
						summaries.push({
							workItemPath,
							originalValue: changeOrder.originalValue,
							componentType: changeOrder.componentType,
							unitOfMeasure: changeOrder.unitOfMeasure,
							proposedValue: changeOrder.proposedValue,
						})
					}
				})
			}
		} else {
			// Recursively traverse children
			workItem.childrenWorkItems.forEach((child) => {
				traverseWorkItem(child, workItemPath)
			})
		}
	}

	traverseWorkItem(rootWorkItem, '')
	return summaries
}

export function getWorkItemPath(list: WorkItem[], target: WorkItem) {
	const pathStack: string[] = []

	function traverse(nodeList: WorkItem[]) {
		for (const node of nodeList) {
			pathStack.push(node.name)

			if (node.id === target.id) {
				return true
			}

			if (node.childrenWorkItems && traverse(node.childrenWorkItems)) {
				return true
			}

			pathStack.pop()
		}
		return false
	}

	traverse(list)
	return pathStack.length ? pathStack.join(' > ') : null
}

/** Returns the path for a work item by ID in the form "parent 1 name > parent 2 name > work item name" */
export function getWorkItemPathById(list: WorkItem[], targetId: number): string | null {
	const pathStack: string[] = []

	function traverse(nodeList: WorkItem[]): boolean {
		for (const node of nodeList) {
			pathStack.push(node.name)
			if (node.id === targetId) return true
			if (node.childrenWorkItems && traverse(node.childrenWorkItems)) return true
			pathStack.pop()
		}
		return false
	}
	traverse(list)
	return pathStack.length ? pathStack.join(' > ') : null
}

export function getHierarchyWorkItemIds(workItem: WorkItem[]) {
	const workItemIds: number[] = []
	function traverse(node: WorkItem) {
		workItemIds.push(node.id)
		if (node.childrenWorkItems) {
			node.childrenWorkItems.forEach((child) => traverse(child))
		}
	}
	workItem.forEach((item) => traverse(item))
	return workItemIds
}

export function flattenWorkItemsHierarchy(workItems: WorkItem[]): WorkItem[] {
	const flattenedWorkItems: WorkItem[] = []

	function traverse(items: WorkItem[]) {
		for (const item of items) {
			flattenedWorkItems.push(item)
			if (item.childrenWorkItems && item.childrenWorkItems.length > 0) {
				traverse(item.childrenWorkItems)
			}
		}
	}

	traverse(workItems)
	return flattenedWorkItems
}

/** Returns work items whose name contains searchText (case-insensitive). Empty search returns all items. */
export function filterWorkItemsByName(
	workItems: WorkItem[],
	searchText: string,
): WorkItem[] {
	const trimmed = searchText.trim()
	if (!trimmed) return workItems
	const lower = trimmed.toLowerCase()
	return workItems.filter((item) => item.name?.toLowerCase().includes(lower))
}

/**
 * Finds the given work items (by id) within a hierarchy and returns each found node
 * with its full subtree (descendants). Nodes are returned in hierarchy order.
 */
export function findWorkItemsWithDescendants(
	hierarchy: WorkItem[],
	targetWorkItems: WorkItem[],
): WorkItem[] {
	const targetIds = new Set(targetWorkItems.map((wi) => wi.id))
	const result: WorkItem[] = []

	function traverse(nodes: WorkItem[]) {
		for (const node of nodes) {
			if (targetIds.has(node.id)) {
				result.push(node)
			}
			if (node.childrenWorkItems?.length) {
				traverse(node.childrenWorkItems)
			}
		}
	}

	traverse(hierarchy)
	return result
}

/**
 * Flattens the hierarchy, filters by search text (name), then returns each matching
 * work item from the hierarchy with its full subtree (descendants).
 */
export function searchWorkItemsHierarchy(
	hierarchy: WorkItem[],
	searchText: string,
): WorkItem[] {
	const flat = flattenWorkItemsHierarchy(hierarchy)
	const filtered = filterWorkItemsByName(flat, searchText)
	return findWorkItemsWithDescendants(hierarchy, filtered)
}

export function buildHierarchy(
	workItems: WorkItem[],
	tasks: WorkItemTask[],
	reworks: ReworkTask[],
	constraints: Constraint[],
) {
	const workItemMap = new Map<number, WorkItem>()

	workItems.forEach((workItem) => {
		workItemMap.set(workItem.id, {
			...workItem,
			tasks: [],
			reworks: [],
			childrenWorkItems: [],
			constraintCount: 0,
		})
	})

	const rootWorkItems = workItems.filter(
		(workItem) =>
			workItem.parentId === null || !workItems.some((wi) => wi.id === workItem.parentId),
	)
	const tasksByWorkItem = new Map<number, WorkItemTask[]>()
	tasks.forEach((task) => {
		const workItemId = task.workItemId!
		if (workItemId && !tasksByWorkItem.has(workItemId)) {
			tasksByWorkItem.set(workItemId, [])
		}
		if (workItemId) {
			tasksByWorkItem.get(workItemId)!.push(task)
		}
	})

	const reworksByTask = new Map<number, ReworkTask[]>()
	reworks.forEach((rework) => {
		const taskId = rework.workItemTaskId!
		if (taskId && !reworksByTask.has(taskId)) {
			reworksByTask.set(taskId, [])
		}
		if (taskId) {
			reworksByTask.get(taskId)!.push(rework)
		}
	})

	const constraintByWorkItem = new Map<number, Constraint[]>()
	constraints.forEach((constraint) => {
		if (constraint.workItemId && !constraintByWorkItem.has(constraint.workItemId)) {
			constraintByWorkItem.set(constraint.workItemId, [])
		}
		if (constraint.workItemId) {
			constraintByWorkItem.get(constraint.workItemId)!.push(constraint)
		}
	})

	function processWorkItem(workItem: WorkItem): WorkItem {
		const workItemTasks = tasksByWorkItem.get(workItem.id) || []
		workItem.tasks = workItemTasks.map((task) => ({
			...task,
			reworks: reworksByTask.get(task.id) || [],
			
		}))
		workItem.constraints = constraintByWorkItem.get(workItem.id) || []

		if (workItems.some((wi) => wi.parentId === workItem.id)) {
			workItem.childrenWorkItems = workItems
				.filter((wi) => wi.parentId === workItem.id)
				.map((wi) => processWorkItem(wi))
			workItem.constraints = workItem.constraints?.concat(
				workItem.childrenWorkItems.flatMap((wi) => wi.constraints || []),
			)
		}
		
		return workItem
	}

	return rootWorkItems.map((workItem) => processWorkItem(workItem))
}

export const getWorkItemParentsIds = (workItem: WorkItem, allWorkItems: WorkItem[]) => {
	const parentsIds: number[] = []
	function traverseUp(node: WorkItem) {
		if (node.parentId) {
			parentsIds.push(node.parentId)
			const parent = allWorkItems.find((wi) => wi.id === node.parentId)
			if (parent) {
				traverseUp(parent)
			}
		}
		
	}
	traverseUp(workItem)
	return parentsIds
}
